import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, updateDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';

const IRS_RATE = 0.70;

export default function TripDetail() {
  const { tripId } = useParams();
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  const [trip, setTrip] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');

  const [editForm, setEditForm] = useState({
    startAddress: '',
    endAddress: '',
    distance: '',
    date: '',
    purpose: 'Business',
    notes: '',
  });

  useEffect(() => {
    async function fetchTrip() {
      try {
        const snap = await getDoc(doc(db, 'users', currentUser.uid, 'trips', tripId));
        if (snap.exists()) {
          const data = { id: snap.id, ...snap.data() };
          setTrip(data);
          setEditForm({
            startAddress: data.startAddress || '',
            endAddress: data.endAddress || '',
            distance: data.distance?.toString() || '',
            date: data.date ? data.date.split('T')[0] : '',
            purpose: data.purpose || 'Business',
            notes: data.notes || '',
          });
        }
      } catch (err) {
        console.error('Error fetching trip:', err);
        setError('Failed to load trip details.');
      }
      setLoading(false);
    }
    fetchTrip();
  }, [currentUser.uid, tripId]);

  async function handleSave() {
    if (!editForm.distance || parseFloat(editForm.distance) <= 0) {
      setError('Distance must be greater than 0.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const dist = parseFloat(editForm.distance);
      await updateDoc(doc(db, 'users', currentUser.uid, 'trips', tripId), {
        startAddress: editForm.startAddress.slice(0, 200),
        endAddress: editForm.endAddress.slice(0, 200),
        distance: dist,
        deduction: parseFloat((dist * (trip.irsRate || IRS_RATE)).toFixed(2)),
        date: editForm.date ? new Date(editForm.date + 'T12:00:00').toISOString() : trip.date,
        purpose: editForm.purpose,
        notes: editForm.notes.slice(0, 500),
      });
      setTrip((prev) => ({
        ...prev,
        startAddress: editForm.startAddress,
        endAddress: editForm.endAddress,
        distance: dist,
        deduction: parseFloat((dist * (prev.irsRate || IRS_RATE)).toFixed(2)),
        date: editForm.date ? new Date(editForm.date + 'T12:00:00').toISOString() : prev.date,
        purpose: editForm.purpose,
        notes: editForm.notes,
      }));
      setEditing(false);
    } catch (err) {
      setError('Failed to save changes.');
      console.error('Update error:', err);
    }
    setSaving(false);
  }

  async function handleDelete() {
    setDeleting(true);
    try {
      await deleteDoc(doc(db, 'users', currentUser.uid, 'trips', tripId));
      navigate('/dashboard');
    } catch (err) {
      setError('Failed to delete trip.');
      console.error('Delete error:', err);
    }
    setDeleting(false);
  }

  if (loading) {
    return <div className="screen-container"><div className="loading">Loading...</div></div>;
  }

  if (!trip) {
    return (
      <div className="screen-container">
        <div className="empty-state">
          <p>Trip not found.</p>
          <button className="btn btn-secondary" onClick={() => navigate('/dashboard')}>Back to Trips</button>
        </div>
      </div>
    );
  }

  const deduction = trip.deduction || (trip.distance * (trip.irsRate || IRS_RATE));
  const tripDate = trip.date ? new Date(trip.date) : null;
  const hasCoordinates = trip.coordinates && trip.coordinates.length > 0;

  if (editing) {
    return (
      <div className="screen-container">
        <h2>Edit Trip</h2>
        {error && <div className="error-message">{error}</div>}

        <div className="manual-trip-form">
          <div className="trip-route-fields">
            <div className="route-field">
              <div className="route-dot start" />
              <div className="form-group">
                <input
                  type="text"
                  value={editForm.startAddress}
                  onChange={(e) => setEditForm((f) => ({ ...f, startAddress: e.target.value }))}
                  placeholder="Start location"
                />
              </div>
            </div>
            <div className="route-line" />
            <div className="route-field">
              <div className="route-dot end" />
              <div className="form-group">
                <input
                  type="text"
                  value={editForm.endAddress}
                  onChange={(e) => setEditForm((f) => ({ ...f, endAddress: e.target.value }))}
                  placeholder="End location"
                />
              </div>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Distance (miles)</label>
              <input
                type="number"
                value={editForm.distance}
                onChange={(e) => setEditForm((f) => ({ ...f, distance: e.target.value }))}
                step="0.1"
                min="0"
              />
            </div>
            <div className="form-group">
              <label>Date</label>
              <input
                type="date"
                value={editForm.date}
                onChange={(e) => setEditForm((f) => ({ ...f, date: e.target.value }))}
              />
            </div>
          </div>

          <div className="form-group">
            <label>Purpose</label>
            <div className="purpose-toggle">
              {['Business', 'Personal', 'Medical', 'Charity'].map((p) => (
                <button
                  key={p}
                  className={`purpose-btn ${editForm.purpose === p ? 'active' : ''}`}
                  onClick={() => setEditForm((f) => ({ ...f, purpose: p }))}
                  type="button"
                >
                  {p}
                </button>
              ))}
            </div>
          </div>

          <div className="form-group">
            <label>Notes (optional)</label>
            <input
              type="text"
              value={editForm.notes}
              onChange={(e) => setEditForm((f) => ({ ...f, notes: e.target.value }))}
              placeholder="Client name, trip purpose, etc."
            />
          </div>

          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
          <button className="btn btn-secondary" onClick={() => { setEditing(false); setError(''); }} style={{ marginTop: '8px' }}>
            Cancel
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="screen-container">
      <div className="trip-detail-header">
        <button className="trip-detail-back" onClick={() => navigate('/dashboard')}>
          <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
            <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z"/>
          </svg>
        </button>
        <h2>Trip Details</h2>
      </div>

      {error && <div className="error-message">{error}</div>}

      {/* Deduction hero */}
      <div className="trip-detail-hero">
        <span className="trip-detail-deduction">${deduction.toFixed(2)}</span>
        <span className="trip-detail-deduction-label">Tax Deduction</span>
      </div>

      {/* Route */}
      <div className="trip-detail-route">
        <div className="trip-detail-route-point">
          <div className="route-dot start" />
          <span>{trip.startAddress || 'Start location'}</span>
        </div>
        <div className="route-line" />
        <div className="trip-detail-route-point">
          <div className="route-dot end" />
          <span>{trip.endAddress || 'End location'}</span>
        </div>
      </div>

      {/* Stats grid */}
      <div className="trip-detail-stats">
        <div className="trip-detail-stat">
          <span className="trip-detail-stat-value">{trip.distance?.toFixed(1)} mi</span>
          <span className="trip-detail-stat-label">Distance</span>
        </div>
        <div className="trip-detail-stat">
          <span className="trip-detail-stat-value">
            {tripDate ? tripDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '--'}
          </span>
          <span className="trip-detail-stat-label">Date</span>
        </div>
        <div className="trip-detail-stat">
          <span className="trip-detail-stat-value">{trip.purpose || 'Business'}</span>
          <span className="trip-detail-stat-label">Purpose</span>
        </div>
        {trip.durationMinutes && (
          <div className="trip-detail-stat">
            <span className="trip-detail-stat-value">
              {trip.durationMinutes >= 60
                ? `${Math.floor(trip.durationMinutes / 60)}h ${Math.round(trip.durationMinutes % 60)}m`
                : `${Math.round(trip.durationMinutes)}m`}
            </span>
            <span className="trip-detail-stat-label">Duration</span>
          </div>
        )}
      </div>

      {/* Meta info */}
      <div className="trip-detail-meta">
        <div className="trip-detail-meta-row">
          <span className="trip-detail-meta-label">IRS Rate</span>
          <span className="trip-detail-meta-value">${(trip.irsRate || IRS_RATE).toFixed(2)}/mile</span>
        </div>
        <div className="trip-detail-meta-row">
          <span className="trip-detail-meta-label">Tracking</span>
          <span className="trip-detail-meta-value">
            {trip.trackingMethod === 'manual-gps' ? 'GPS' : trip.trackingMethod === 'auto-detect' ? 'Auto-detected' : 'Manual'}
          </span>
        </div>
        {hasCoordinates && (
          <div className="trip-detail-meta-row">
            <span className="trip-detail-meta-label">GPS Points</span>
            <span className="trip-detail-meta-value">{trip.coordinates.length}</span>
          </div>
        )}
        {trip.notes && (
          <div className="trip-detail-meta-row">
            <span className="trip-detail-meta-label">Notes</span>
            <span className="trip-detail-meta-value">{trip.notes}</span>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="trip-detail-actions">
        <button className="btn btn-secondary" onClick={() => setEditing(true)}>
          Edit Trip
        </button>
        <button className="btn btn-danger-outline" onClick={() => setShowDeleteModal(true)}>
          Delete Trip
        </button>
      </div>

      {/* Delete confirmation modal */}
      {showDeleteModal && (
        <div className="modal-overlay" onClick={() => !deleting && setShowDeleteModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Delete Trip</h3>
            <p>Are you sure you want to delete this trip? This action cannot be undone.</p>
            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={() => setShowDeleteModal(false)} disabled={deleting}>
                Keep Trip
              </button>
              <button className="btn btn-danger" onClick={handleDelete} disabled={deleting}>
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
